/**
 * Product Card Mini
 * Handles add-to-cart functionality and marquee animations for mini product cards.
 */

(function() {
  'use strict';

  /**
   * Initialize all product card mini components
   */
  function init() {
    initAddToCartButtons();
    initVariantSelectors();
    initTitleMarquees();
  }

  /**
   * Initialize add to cart buttons
   */
  function initAddToCartButtons() {
    document.querySelectorAll('[data-product-card-mini] [data-add-to-cart]').forEach(button => {
      if (button.hasAttribute('data-mini-initialized')) return;
      button.setAttribute('data-mini-initialized', 'true');

      button.addEventListener('click', handleAddToCart);
    });
  }

  /**
   * Handle add to cart click
   */
  async function handleAddToCart(e) {
    e.preventDefault();
    const button = e.currentTarget;
    const card = button.closest('[data-product-card-mini]');
    const variantId = button.dataset.variantId;

    if (!variantId || button.classList.contains('btn--loading') || button.disabled) return;

    // Add loading state (button snippet uses btn--loading class)
    button.classList.add('btn--loading');
    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: 1
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const addedItem = await response.json();

      // Dispatch cart:item-added event (triggers notification and cart refresh)
      window.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: {
          item: addedItem
        }
      }));

    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      // Remove loading state
      button.classList.remove('btn--loading');
      button.disabled = false;
    }
  }

  /**
   * Initialize variant selectors (Arco select component)
   */
  function initVariantSelectors() {
    document.querySelectorAll('[data-product-card-mini] .product-card-mini__variants').forEach(variantsContainer => {
      if (variantsContainer.hasAttribute('data-mini-initialized')) return;
      variantsContainer.setAttribute('data-mini-initialized', 'true');

      // Get variant options data
      const variantOptions = JSON.parse(variantsContainer.dataset.variantOptions || '[]');

      // Find the Arco select component or native select
      const arcoSelect = variantsContainer.querySelector('[data-select]');
      const nativeSelect = variantsContainer.querySelector('.select__native') || variantsContainer.querySelector('.product-card-mini__native-select');
      
      if (!nativeSelect) return;

      // Listen for Arco select:change custom event (bubbles up from the select component)
      if (arcoSelect) {
        arcoSelect.addEventListener('select:change', (e) => {
          handleVariantChange(variantsContainer, nativeSelect, variantOptions);
        });
      }

      // Also listen for native change event (fallback and for form submission sync)
      nativeSelect.addEventListener('change', () => {
        handleVariantChange(variantsContainer, nativeSelect, variantOptions);
      });
    });
  }

  /**
   * Handle variant selection change
   */
  function handleVariantChange(variantsContainer, nativeSelect, variantOptions) {
    const card = variantsContainer.closest('[data-product-card-mini]');
    const selectedValue = nativeSelect.value;
    const addButton = card.querySelector('[data-add-to-cart]');
    const priceContainer = card.querySelector('.product-card-mini__price');
    const imageEl = card.querySelector('.product-card-mini__image');

    if (!addButton || !selectedValue) return;

    // Find the selected variant data
    const selectedVariant = variantOptions.find(v => String(v.value) === String(selectedValue));
    
    if (!selectedVariant) return;

    // Update variant ID on add button
    addButton.dataset.variantId = selectedVariant.value;

    // Update image if variant has one
    if (imageEl && selectedVariant.image) {
      imageEl.src = selectedVariant.image;
    }

    // Update price display
    if (priceContainer) {
      const price = selectedVariant.price;
      const comparePrice = selectedVariant.compare_price;

      if (comparePrice && comparePrice > price) {
        priceContainer.innerHTML = `
          <span class="product-card-mini__price--sale">${formatMoney(price)}</span>
          <span class="product-card-mini__price--compare">${formatMoney(comparePrice)}</span>
        `;
      } else {
        priceContainer.innerHTML = `<span>${formatMoney(price)}</span>`;
      }
    }

    // Update button state based on availability
    // available is true when: inventory not tracked, OR inventory > 0, OR oversell allowed
    const isAvailable = selectedVariant.available === true;
    if (!isAvailable) {
      addButton.disabled = true;
      const textEl = addButton.querySelector('.btn__text');
      if (textEl) textEl.textContent = 'Sold out';
      const icon = addButton.querySelector('.btn__icon');
      if (icon) icon.style.display = 'none';
    } else {
      addButton.disabled = false;
      const textEl = addButton.querySelector('.btn__text');
      if (textEl) textEl.textContent = 'Add';
      const icon = addButton.querySelector('.btn__icon');
      if (icon) icon.style.display = '';
    }
  }

  /**
   * Format money using theme format
   */
  function formatMoney(cents, format) {
    if (window.formatMoney) {
      return window.formatMoney(cents, format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Initialize marquee animations for overflowing titles
   */
  function initTitleMarquees() {
    if (typeof gsap === 'undefined') return;

    document.querySelectorAll('[data-product-card-mini] [data-title-wrapper]').forEach(wrapper => {
      if (wrapper.hasAttribute('data-marquee-initialized')) return;
      wrapper.setAttribute('data-marquee-initialized', 'true');

      const title = wrapper.querySelector('[data-title]');
      if (!title) return;

      // Check if text overflows
      const wrapperWidth = wrapper.offsetWidth;
      const titleWidth = title.scrollWidth;

      if (titleWidth > wrapperWidth) {
        wrapper.classList.add('is-marquee');
        
        // Create infinite marquee animation
        gsap.to(title, {
          x: -titleWidth / 2,
          duration: titleWidth / 50,
          ease: 'none',
          repeat: -1,
          repeatDelay: 2
        });
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize when cart updates (for dynamically added cards)
  document.addEventListener('cart:updated', () => {
    setTimeout(init, 100);
  });

  // Expose init function globally for manual re-initialization
  window.ProductCardMini = { init };
})();
